import { Thoughts } from '@/components/assistant/Thoughts'
import { useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { cn } from '@repo/ui/utils'
import { BellRing, X } from 'lucide-react'
import { IoSparklesSharp } from 'react-icons/io5'

type AssistantHeaderProps = {
  hasAlerts: boolean
  isThinking: boolean
  loadingTransKeys: string[] | undefined
  onToggleAlerts: () => void
  openAlerts: boolean
  unreadAlertsCount: number
}

export const AssistantHeader = ({
  hasAlerts,
  isThinking,
  loadingTransKeys,
  onToggleAlerts,
  openAlerts,
  unreadAlertsCount,
}: AssistantHeaderProps) => {
  const { t, isEN } = useT({ keyPrefix: 'assistant' })

  const alertMessageShown = openAlerts || (!isThinking && unreadAlertsCount > 0)
  const AiSpeaking = isThinking && loadingTransKeys
  const hasUnreadAlerts = unreadAlertsCount > 0

  return (
    <header className="mr-4 flex items-center justify-between gap-4 px-4 pt-4 lg:px-0 lg:pt-0">
      <div className="bg-primary-900 mb-4 ml-3 flex w-fit flex-shrink-0 flex-col gap-2 rounded-full bg-opacity-85 py-3.5 pl-6 pr-8">
        <div className="text-primary-100 flex select-none items-center gap-2">
          <IoSparklesSharp
            className={cn(
              'h-6 w-6 -rotate-12',
              (AiSpeaking || alertMessageShown) && 'text-emerald-500',
              AiSpeaking && 'animate-pulse',
            )}
          />
          <span
            className={cn(
              'flex flex-1 text-lg',
              (AiSpeaking || alertMessageShown) && 'text-emerald-500',
              AiSpeaking && 'animate-pulse',
            )}
          >
            {AiSpeaking || alertMessageShown
              ? isEN
                ? 'CaseAI:'
                : 'CaseAI :'
              : 'CaseAI'}
          </span>
          <span className="lowercase">
            {alertMessageShown
              ? t('alert.assistantMessage', { count: unreadAlertsCount })
              : AiSpeaking && (
                  <Thoughts
                    isThinking={isThinking}
                    loadingTransKeys={loadingTransKeys}
                  />
                )}
          </span>
        </div>
      </div>
      {/* Alert Toggle Button */}
      {hasAlerts && (
        <TooltipWrapper
          content={openAlerts ? t('alert.hideAlerts') : t('alert.showAlerts')}
        >
          <div
            className={cn(
              'z-0 mb-4 flex h-14 w-14 flex-shrink-0 cursor-pointer select-none items-center justify-center rounded-full bg-opacity-85 text-lg shadow transition-all hover:bg-opacity-95',
              openAlerts
                ? 'text-highlight bg-white'
                : 'bg-highlight shadow-primary-600/50 text-white',
            )}
            onClick={onToggleAlerts}
          >
            {openAlerts ? (
              <X className="size-5" />
            ) : (
              <div className={cn(hasUnreadAlerts && 'pt-2')}>
                <BellRing className={cn(hasUnreadAlerts && 'animate-bounce')} />
              </div>
            )}
          </div>
        </TooltipWrapper>
      )}
    </header>
  )
}

export const AssistantHeaderLight = ({
  isThinking,
  loadingTransKeys,
}: {
  isThinking: boolean
  loadingTransKeys: string[] | undefined
}) => {
  const { isEN } = useT({ keyPrefix: 'assistant' })
  const AiSpeaking = isThinking && loadingTransKeys
  return (
    <header className="mr-4 flex items-center justify-between gap-4 px-4 pt-4 lg:px-0 lg:pt-0">
      <div className="bg-primary-900 mb-4 ml-3 flex w-fit flex-shrink-0 flex-col gap-2 rounded-full bg-opacity-85 py-3.5 pl-6 pr-8">
        <div className="text-primary-100 flex select-none items-center gap-2">
          <IoSparklesSharp
            className={cn(
              'h-6 w-6 -rotate-12',
              AiSpeaking && 'text-emerald-500',
              AiSpeaking && 'animate-pulse',
            )}
          />
          <span
            className={cn(
              'flex flex-1 text-lg',
              AiSpeaking && 'text-emerald-500',
              AiSpeaking && 'animate-pulse',
            )}
          >
            {AiSpeaking ? (isEN ? 'CaseAI:' : 'CaseAI :') : 'CaseAI'}
          </span>
          <span className="lowercase">
            {AiSpeaking && (
              <Thoughts
                isThinking={isThinking}
                loadingTransKeys={loadingTransKeys}
              />
            )}
          </span>
        </div>
      </div>
    </header>
  )
}
